import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { getCompanyDetails, getOwnershipGraph, getCompanyFinancials } from '../services/apiClient'
import {
  CVRCompanyDetails,
  CVROwnershipStructure,
  CVRParticipant,
  OwnershipGraphData,
  CompanyFinancials,
} from '../types'
import { Container, Tabs } from '../components/ui'
import type { TabDef } from '../components/ui'
import IdentityBar from '../components/profile/IdentityBar'
import WatchlistControl from '../components/WatchlistControl'
import CompanyProfileSkeleton from '../components/profile/CompanyProfileSkeleton'
import { deriveSummaryCounts } from '../components/profile/SummaryStrip'
import EjerskabOverview from '../components/profile/EjerskabOverview'
import RecentChanges, { collectAllCompanyActivity } from '../components/profile/RecentChanges'
import RoleListSection from '../components/profile/RoleListSection'
import Stamdata from '../components/profile/Stamdata'
import { navigateBackOrHome } from '../utils/appNavigation'
import { companyCountryFlagCode } from '../utils/format'
import { usePageTitle } from '../hooks/usePageTitle'

/**
 * Company profile — the journalistic centerpiece.
 *
 * Both company details and the ownership graph are fetched in parallel so
 * each can render as soon as it lands — neither blocks the other.
 *
 * Tab state is mirrored to `?tab=…` for shareability.
 */
const EMPTY_OWNERSHIP: CVROwnershipStructure = {
  beneficialOwners: [],
  boardMembers: [],
  management: [],
  auditors: [],
  ownershipRegister: [],
  other: [],
}

type TabId = 'ejerskab' | 'aktivitet' | 'ledelse' | 'stamdata'
const TAB_IDS: TabId[] = ['ejerskab', 'aktivitet', 'ledelse', 'stamdata']

function readTab(raw: string | null): TabId {
  if (raw === 'aendringer') return 'aktivitet'
  return (TAB_IDS as string[]).includes(raw ?? '') ? (raw as TabId) : 'ejerskab'
}

const CompanyProfilePage: React.FC = () => {
  const { cvrNumber } = useParams<{ cvrNumber: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const [company, setCompany]           = useState<CVRCompanyDetails | null>(null)
  const [loading, setLoading]           = useState(true)
  const [error, setError]               = useState<string | null>(null)

  const [graph, setGraph]               = useState<OwnershipGraphData | null>(null)
  const [graphLoading, setGraphLoading] = useState(true)

  const [financials, setFinancials]             = useState<CompanyFinancials | null>(null)
  const [financialsLoading, setFinancialsLoading] = useState(true)

  const activeTab: TabId = readTab(searchParams.get('tab'))

  const handleTabChange = (id: string) => {
    const next = readTab(id)
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next === 'ejerskab') {
          params.delete('tab')
        } else {
          params.set('tab', next)
        }
        return params
      },
      { replace: true }
    )
  }

  useEffect(() => {
    if (!cvrNumber) return
    let cancelled = false

    // ── Company details ──────────────────────────────────────────────────────
    setLoading(true)
    setError(null)
    getCompanyDetails(cvrNumber)
      .then((details) => {
        if (!cancelled) setCompany(details)
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Kunne ikke hente virksomheden.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    // ── Ownership graph — independent, non-blocking ──────────────────────────
    setGraphLoading(true)
    setGraph(null)
    getOwnershipGraph(cvrNumber)
      .then((data) => {
        if (!cancelled) setGraph(data)
      })
      .catch(() => {
        // Graph failures are silent — text sections still render
        if (!cancelled) setGraph(null)
      })
      .finally(() => {
        if (!cancelled) setGraphLoading(false)
      })

    // ── Financials — independent, non-blocking ───────────────────────────────
    setFinancialsLoading(true)
    setFinancials(null)
    getCompanyFinancials(cvrNumber)
      .then((data) => {
        if (!cancelled) setFinancials(data)
      })
      .catch(() => {
        if (!cancelled) setFinancials(null)
      })
      .finally(() => {
        if (!cancelled) setFinancialsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [cvrNumber])

  const ownership = company?.ownership ?? EMPTY_OWNERSHIP
  const founders: CVRParticipant[] = company?.founders ?? []
  const rootParticipants = useMemo(
    () => [
      ...founders,
      ...ownership.beneficialOwners,
      ...ownership.ownershipRegister,
      ...ownership.management,
      ...ownership.boardMembers,
      ...ownership.auditors,
      ...ownership.other,
    ],
    [founders, ownership],
  )
  const counts = useMemo(
    () => deriveSummaryCounts(ownership, new Date(), founders, company?.registryEvents ?? []),
    [ownership, founders, company?.registryEvents]
  )
  const allChangesCount = useMemo(
    () =>
      collectAllCompanyActivity(
        ownership,
        company?.registryEvents ?? [],
        new Date(),
        founders
      ).length,
    [ownership, company?.registryEvents, founders]
  )

  const pageTitle = useMemo(() => {
    if (loading) return 'Virksomhed'
    if (error || !company) return 'Virksomhed ikke fundet'
    return company.navn || `CVR ${company.cvrNummer}`
  }, [loading, error, company])
  usePageTitle(pageTitle)

  if (loading) {
    return (
      <Container width="prose" className="py-10">
        <BackLink />
        <div className="mt-6" data-testid="company-profile-head">
          <CompanyProfileSkeleton />
        </div>
      </Container>
    )
  }

  if (error || !company) {
    return (
      <Container width="prose" className="py-16">
        <BackLink />
        <h1 className="font-serif text-2xl text-ink mt-6">
          Kunne ikke vise virksomheden
        </h1>
        <p className="mt-3 text-sm text-signal-900 bg-signal-50 border border-signal-200 rounded-sm px-3 py-2">
          {error || 'Virksomheden blev ikke fundet.'}
        </p>
      </Container>
    )
  }

  const foundersCount = founders.length
  const ownersTotal =
    foundersCount +
    ownership.beneficialOwners.filter((p) => p.role.isActive).length +
    ownership.ownershipRegister.filter(
      (p) => p.role.legalRegisterShowInPrimaryList === true
    ).length

  const tabs: TabDef[] = [
    {
      id: 'ejerskab',
      label: 'Ejerskab',
      count: ownersTotal > 0 ? ownersTotal : null,
      tone: counts.foreignBeneficial > 0 ? 'signal' : 'neutral',
    },
    {
      id: 'aktivitet',
      label: 'Aktivitet',
      count: allChangesCount > 0 ? allChangesCount : null,
    },
    {
      id: 'ledelse',
      label: 'Ledelse',
      count: counts.management > 0 ? counts.management : null,
    },
    { id: 'stamdata', label: 'Stamdata' },
  ]

  return (
    <Container width="prose" className="py-10">
      <BackLink />

      <header className="mt-6 pb-4" data-testid="company-profile-head">
        <IdentityBar
          company={company}
          financials={financials}
          financialsLoading={financialsLoading}
          titleAction={
            <WatchlistControl
              iconOnly
              iconSize="lg"
              entityType="company"
              entityId={String(company.cvrNummer)}
              displayName={company.navn || `CVR ${company.cvrNummer}`}
              countryCode={companyCountryFlagCode(company)}
            />
          }
        />
        <Tabs
          dense
          tabs={tabs}
          active={activeTab}
          onChange={handleTabChange}
          ariaLabel="Virksomhedssektioner"
          className="mt-4"
        />
      </header>

      <TabPanel id="ejerskab" active={activeTab}>
        <EjerskabOverview
          founders={founders}
          foundingManagementAtParents={company.foundingManagementAtParents}
          legalOwners={ownership.ownershipRegister}
          beneficialOwners={ownership.beneficialOwners}
          rootParticipants={rootParticipants}
          graph={graph}
          graphLoading={graphLoading}
          startdato={company.startdato}
        />
      </TabPanel>

      <TabPanel id="aktivitet" active={activeTab}>
        <RecentChanges
          ownership={ownership}
          registryEvents={company.registryEvents}
          scope="all"
        />
      </TabPanel>

      <TabPanel id="ledelse" active={activeTab}>
        <RoleListSection title="Direktion" participants={ownership.management} defaultOpen compact />
        <RoleListSection title="Bestyrelse" participants={ownership.boardMembers} defaultOpen compact />
        <RoleListSection title="Revision" participants={ownership.auditors} defaultOpen compact />
        <RoleListSection title="Andre roller" participants={ownership.other} defaultOpen compact />
      </TabPanel>

      <TabPanel id="stamdata" active={activeTab}>
        <Stamdata company={company} />
      </TabPanel>
    </Container>
  )
}

interface TabPanelProps {
  id: TabId
  active: TabId
  children: React.ReactNode
}

const TabPanel: React.FC<TabPanelProps> = ({ id, active, children }) => {
  const hidden = id !== active
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${id}`}
      aria-labelledby={`tab-${id}`}
      hidden={hidden}
      className={hidden ? '' : 'mt-6'}
    >
      {!hidden && children}
    </div>
  )
}

const BackLink: React.FC = () => {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigateBackOrHome(navigate)}
      className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink bg-transparent border-0 p-0 cursor-pointer font-sans text-left"
    >
      <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
      Tilbage
    </button>
  )
}

export default CompanyProfilePage
